// @flow
import { klona } from "klona/full";

import { has, isPlainObject } from "../../utils/helpers/objects";

import LayoutHintRuleCollection from "./layouthint-rules/LayoutHintRuleCollection";

import CaptchaAttributeModel from "./CaptchaAttributeModel";
import ChoiceAttributeModel from "./ChoiceAttributeModel";
import DatetimeAttributeModel from "./DatetimeAttributeModel";
import HelptextAttributeModel from "./HelptextAttributeModel";
import LabelAttributeModel from "./LabelAttributeModel";
import MemoAttributeModel from "./MemoAttributeModel";
import MoneyAttributeModel from "./MoneyAttributeModel";
import NumberAttributeModel from "./NumberAttributeModel";
import PasswordAttributeModel from "./PasswordAttributeModel";
import CompositeAttributeModel from "./CompositeAttributeModel";
import StringAttributeModel from "./StringAttributeModel";
import UploadAttributeModel from "./UploadAttributeModel";
import XMLAttributeModel from "./XMLAttributeModel";
import BooleanAttributeModel from "./BooleanAttributeModel";

import AttributeDataHelper from "./AttributeDataHelper";

import type { AttributeType, ModelOptions } from "../types";

/**
 * @private
 */
const getChildrenKeys = (children: Array<Object>): Array<Object> => {
  if (children) {
    return children.map((child) => {
      const [childKey] = Object.keys(child);
      const isComposite =
        child[childKey].type === "range" ||
        child[childKey].type === "composite";

      return {
        key: childKey,
        dynamicschemaId: child[childKey]?.dynamicschemaId || undefined,
        children: isComposite ? getChildrenKeys(child[childKey]?.children) : [],
      };
    });
  }

  return [];
};

/**
 * @private
 */
const getAttributeModel = (contributions: Object): ?Class<AttributeType> => {
  const ATTRIBUTE_MODELS = [
    ChoiceAttributeModel,
    BooleanAttributeModel,

    HelptextAttributeModel,
    LabelAttributeModel,

    XMLAttributeModel,
    MemoAttributeModel,
    StringAttributeModel,

    DatetimeAttributeModel,

    MoneyAttributeModel,
    NumberAttributeModel,
    PasswordAttributeModel,

    UploadAttributeModel,
    CompositeAttributeModel,
    CaptchaAttributeModel,
  ];

  return ATTRIBUTE_MODELS.find((model) =>
    model.isApplicableModel(contributions),
  );
};

/**
 * @private
 */
const updateContributions = (
  key: string,
  data: Object,
  contributions: Object,
): Object => {
  const isComposite =
    contributions.type === "array" &&
    has(contributions, "children") &&
    isPlainObject(data) &&
    Array.isArray(data[key]);

  return {
    ...contributions,
    type: isComposite ? "composite" : contributions.type,
  };
};

/**
 * @private
 */
const updateData = (
  key: string,
  data: Object,
  contributions: Object,
): Object => {
  const newData = klona(data);

  if (contributions.dynamicschemaId) {
    newData.dynamicschemaId = contributions.dynamicschemaId;
  }

  return newData;
};

/**
 * Create an attribute based on data and contributions
 */
const createAttribute = (
  key: string,
  data: Object | Array<Object>,
  contributions: Object,
  modelOptions?: ModelOptions,
): AttributeType => {
  const updatedContributions = updateContributions(key, data, contributions);
  const updatedData = updateData(key, data, contributions);

  const Model = getAttributeModel(updatedContributions);

  if (!Model) {
    throw new Error("Could not find model");
  }

  const childrenKeys = getChildrenKeys(updatedContributions.children ?? []);
  const dataHelper = new AttributeDataHelper(
    updatedData,
    key,
    childrenKeys,
  ).getData();

  const attribute = new Model(dataHelper, updatedContributions, modelOptions);

  return LayoutHintRuleCollection.setLayoutHintRules(attribute);
};

export default createAttribute;
